/**
 * Desktop auth synchronisation — two responsibilities:
 *
 * 1. On boot, ask the Electron main process for the baked-in tenant slug
 *    and prime the api.js in-memory store so getTenantSlug() works even
 *    before the renderer URL query param is processed.
 *
 * 2. Every 5 s, push the current token / tenant / propertyId to the main
 *    process so the SyncWorker can attach the correct auth headers.
 */

import { getToken, getTenantSlug, getPropertyId, setTenantSlug } from '../services/api.js';

export async function syncAuthToMain() {
  if (!window.pms?.setAuth) return;
  await window.pms.setAuth({
    token:      getToken()      || '',
    tenant:     getTenantSlug(),
    propertyId: getPropertyId() || '',
  });
}

/**
 * Called once from desktopBridge.js at renderer boot.
 *
 * Asks the main process for the baked tenant slug (set via CLI / env / build),
 * then primes the api.js memory store so getTenantSlug() returns the right
 * value immediately — before any network call or user interaction.
 */
export async function primeTenantFromMain() {
  if (!window.pms?.getTenantSlug) return;
  try {
    const slug = await window.pms.getTenantSlug();
    if (slug && slug !== 'default') {
      // setTenantSlug is exported from api.js — see the patch in api.js below
      if (typeof setTenantSlug === 'function') setTenantSlug(slug);
      else {
        // Fallback: write to localStorage so the next getTenantSlug() call picks it up
        localStorage.setItem('hotelmis_tenant_slug', slug);
      }
    }
  } catch (e) {
    console.warn('[desktopAuthSync] primeTenantFromMain failed:', e);
  }
}

export function installDesktopAuthWatcher() {
  if (!window.pms) return;

  // 1. Immediately prime tenant from main process
  primeTenantFromMain().then(() => syncAuthToMain());

  // 2. Periodic push so token refreshes propagate to SyncWorker
  const iv = setInterval(syncAuthToMain, 5000);
  window.addEventListener('beforeunload', () => clearInterval(iv));
}